import {
  PRESENTATION_CUSTOMER_MOBILE,
  PRESENTATION_CUSTOMER_NAME,
  PRESENTATION_FRANCHISE_CODE,
  PRESENTATION_FRANCHISE_USERNAME,
  PRESENTATION_HUB_PATH,
  PRESENTATION_REELS_STARTED_SERVICE_NAME,
} from './presentationConfig';
import {
  createPartnerCheckinQueue,
  fetchActiveVisit,
  startServiceCheckinQueue,
} from '../salon-admin/utils/checkinQueueApi';
import { guestHasName, hasStartedService } from '../video/utils/reelEligibleGuests';
import { handoffFromCheckin, persistSelectedServicesForRecommendations } from '../salon/utils/visitHandoff';
import {
  markSalonFaceCheckedIn,
  setActiveCheckinSession,
  writeVisitHandoff,
} from '../salon/utils/salonFaceSession';
import { setSalonGuestPii, setSalonLastCustomerSnapshot } from '../salon/utils/salonGuestPiiStore';
import { getFranchiseOrganizationId, readFranchiseFlowAuth } from '../shared/franchiseFlowAuth';
import {
  faceIdentityHomePath,
  faceIdentityRoute,
  PASSPORT_BASE,
} from '../face-identity/utils/faceIdentityRoutes';

const PRESENTATION_FLAG = 'enterprise_presentation_mode_v1';
const CHECKIN_CACHE_KEY = 'enterprise_presentation_checkin_id';

let memoryCheckin = null;

function normFranchiseKey(value) {
  return String(value || '').trim().toLowerCase();
}

/** True when kiosk session belongs to the presentation outlet (nt01), not another franchise. */
export function isPresentationFranchiseSession(auth) {
  if (!auth || typeof auth !== 'object') return false;
  const wantUser = normFranchiseKey(PRESENTATION_FRANCHISE_USERNAME);
  const wantCode = normFranchiseKey(PRESENTATION_FRANCHISE_CODE);
  const user = normFranchiseKey(auth.username);
  const code = normFranchiseKey(auth.franchise_code);
  if (wantUser && user === wantUser) return true;
  if (wantCode && code === wantCode) return true;
  return false;
}

export function isPresentationMode() {
  try {
    return sessionStorage.getItem(PRESENTATION_FLAG) === '1';
  } catch {
    return false;
  }
}

/** Router state or session flag — user entered from presentation hub / passport tile. */
export function isPresentationNavContext(state) {
  if (isPresentationMode()) return true;
  const s = state && typeof state === 'object' ? state : {};
  return Boolean(s.passportPresentation || s.fromPresentationHub || s.presentationMode);
}

/** Home target for kiosk flows launched from presentation (else default face-identity home). */
export function resolvePresentationAwareHomePath(pathname = '', state = null) {
  if (isPresentationNavContext(state)) return PRESENTATION_HUB_PATH;
  return faceIdentityHomePath(pathname);
}

export function setPresentationMode(active = true) {
  try {
    sessionStorage.setItem(PRESENTATION_FLAG, active ? '1' : '0');
  } catch {
    /* ignore */
  }
}

export function clearPresentationMode() {
  memoryCheckin = null;
  try {
    sessionStorage.removeItem(PRESENTATION_FLAG);
    sessionStorage.removeItem(CHECKIN_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function rememberCheckin(checkin) {
  if (!checkin?.id) return;
  memoryCheckin = checkin;
  try {
    sessionStorage.setItem(CHECKIN_CACHE_KEY, String(checkin.id));
  } catch {
    /* ignore */
  }
}

function readCachedCheckinId() {
  try {
    return String(sessionStorage.getItem(CHECKIN_CACHE_KEY) || '').trim();
  } catch {
    return '';
  }
}

function presentationDemoServiceLines({ inProgress = false } = {}) {
  const name = PRESENTATION_REELS_STARTED_SERVICE_NAME;
  return [
    {
      name,
      service_name: name,
      service_status: inProgress ? 'in_progress' : 'pending',
    },
  ];
}

function seedLocalGuestFromCheckin(checkin) {
  const handoff = handoffFromCheckin(checkin, {
    entrySource: 'presentation_demo',
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
  });
  if (handoff) writeVisitHandoff(handoff);
  setActiveCheckinSession({
    id: checkin.id,
    code: checkin.customer_code || '',
    status: checkin.status || 'in_service',
    faceUserId: checkin.face_user_id || '',
  });
  markSalonFaceCheckedIn({
    name: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    organizationId: getFranchiseOrganizationId(),
  });
  setSalonGuestPii({
    name: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
  });
  setSalonLastCustomerSnapshot({
    name: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    checkinId: checkin.id,
  });
  const services = Array.isArray(checkin.selected_services) ? checkin.selected_services : [];
  if (services.length) {
    persistSelectedServicesForRecommendations(services);
  }
}

/**
 * Create (or reuse) a started-service check-in for demo guest — products, treatments, reels.
 */
export async function ensurePresentationGuestCheckin({ forceNew = false } = {}) {
  setPresentationMode(true);
  if (!forceNew && memoryCheckin?.id) return memoryCheckin;

  const cachedId = !forceNew ? readCachedCheckinId() : '';
  if (!forceNew && cachedId && memoryCheckin?.id === cachedId) {
    return memoryCheckin;
  }

  const checkin = await createPartnerCheckinQueue({
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    startService: false,
    source: 'presentation_demo',
    selectedServices: presentationDemoServiceLines({ inProgress: false }),
    forceNew: Boolean(forceNew),
  });
  rememberCheckin(checkin);
  seedLocalGuestFromCheckin(checkin);
  return checkin;
}

/**
 * Reels require the same desk rule as live: guest name + at least one started service line.
 */
export async function ensurePresentationReelsCheckin() {
  setPresentationMode(true);
  let checkin = await createPartnerCheckinQueue({
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    startService: false,
    source: 'presentation_demo',
    selectedServices: presentationDemoServiceLines({ inProgress: false }),
    forceNew: true,
  });
  if (!checkin?.id) {
    throw new Error('Could not create demo check-in for reels.');
  }

  checkin = await startServiceCheckinQueue(checkin.id, {
    selectedServices: presentationDemoServiceLines({ inProgress: true }),
  });

  const refreshed =
    (await fetchActiveVisit({ checkinId: checkin.id, includeClosed: true })) || checkin;

  if (!guestHasName(refreshed) || !hasStartedService(refreshed)) {
    throw new Error(
      'Reels need a guest with a started service on the desk. Start the demo service and try again.',
    );
  }

  rememberCheckin(refreshed);
  seedLocalGuestFromCheckin(refreshed);
  return refreshed;
}

export function buildPresentationNavState(extra = {}) {
  const checkin = memoryCheckin;
  return {
    presentationMode: true,
    customerName: PRESENTATION_CUSTOMER_NAME,
    mobile: PRESENTATION_CUSTOMER_MOBILE,
    mobileNumber: PRESENTATION_CUSTOMER_MOBILE,
    checkinId: checkin?.id || readCachedCheckinId() || null,
    fromPresentationHub: true,
    ...extra,
  };
}

export function presentationCheckinForReels() {
  const checkin = memoryCheckin;
  if (!checkin?.id) return null;
  return {
    id: checkin.id,
    customer_name: checkin.customer_name || PRESENTATION_CUSTOMER_NAME,
    customer_code: checkin.customer_code || '',
    mobile: checkin.mobile || PRESENTATION_CUSTOMER_MOBILE,
    selected_services: checkin.selected_services || [],
  };
}

/** Passport kiosk scan — same franchise device session as salon/reels (not guest JWT). */
export function openPresentationPassportScan(navigate) {
  const auth = readFranchiseFlowAuth();
  if (!auth?.organization_id) {
    throw new Error('Sign in to the franchise device before opening Passport scan.');
  }
  navigate(faceIdentityRoute('scan', PASSPORT_BASE), {
    state: buildPresentationNavState({
      passportPresentation: true,
      fromPresentationHub: true,
    }),
  });
}
